import React from 'react';
import { SafeAreaView, StyleSheet, View } from 'react-native';

import { Text } from '~/components/core';
import ScrollbarWrapper from '~/components/core/ScrollbarWrapper';
import DesktopScreenHeader from '../Desktop/DesktopScreenHeader';
import { useStyles } from '~/hooks';
import { FONT_SIZE_M } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    // i18n key for the step banner. Omitted on the first step, which carries its own hero copy.
    title?: string;
    goToScreenName: string;
    setActiveRightTab?: Function;
    children: React.ReactNode;
    // Pinned below the scroll area — the step's primary action.
    footer?: React.ReactNode;
};

/**
 * Shared shell for the Add Funds steps: one back header, one step banner, one scroll area with one
 * horizontal inset.
 *
 * Each step used to build this itself, so the flow changed header, banner style and content inset as
 * you walked through it. Content sizes to its own height here instead of the fixed percentages
 * ('67%', '80%', '20%') that left large empty bands on every screen.
 */
export default function FlowScreen({ title, goToScreenName, setActiveRightTab, children, footer }: Props) {
    const styles = useStyles(stylesGenerator);
    return (
        <SafeAreaView style={styles.safeArea}>
            <DesktopScreenHeader
                goToScreenName={goToScreenName}
                title="add-money"
                setActiveRightTab={setActiveRightTab}
            />
            {title ? (
                <View style={styles.banner}>
                    <Text fontType="LATO_BOLD" style={styles.bannerText}>
                        {title}
                    </Text>
                </View>
            ) : null}
            {/*
             * The padding lives on an inner View rather than contentContainerStyle: on web
             * ScrollbarWrapper is a styled <div> that accepts neither contentContainerStyle nor a
             * RN style object, so anything set there silently disappears.
             */}
            <View style={styles.scrollHost}>
                <ScrollbarWrapper keyboardShouldPersistTaps="handled">
                    <View style={styles.content}>{children}</View>
                </ScrollbarWrapper>
            </View>
            {footer ? <View style={styles.footer}>{footer}</View> : null}
        </SafeAreaView>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        safeArea: {
            flex: 1,
            backgroundColor: theme.background,
        },
        banner: {
            width: '100%',
            backgroundColor: theme.tabIconSelected,
            paddingVertical: scaleSize(8),
            justifyContent: 'center',
            alignItems: 'center',
        },
        bannerText: {
            fontSize: FONT_SIZE_M,
            color: theme.white,
        },
        scrollHost: {
            flex: 1,
            // Lets the scroll area shrink inside the column instead of pushing the footer off-screen.
            minHeight: 0,
        },
        content: {
            width: '100%',
            paddingHorizontal: scaleSize(16),
            paddingTop: scaleSize(20),
            paddingBottom: scaleSize(24),
        },
        footer: {
            paddingHorizontal: scaleSize(16),
            paddingTop: scaleSize(12),
            paddingBottom: scaleSize(16),
            borderTopWidth: StyleSheet.hairlineWidth,
            borderTopColor: theme.border_2,
            backgroundColor: theme.background,
        },
    });
